import * as React from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";

// hCaptcha site key — public, safe to commit. Get one free at hcaptcha.com.
// Set VITE_HCAPTCHA_SITE_KEY in your .env; falls back to hCaptcha's test key.
const HCAPTCHA_SITE_KEY =
   import.meta.env.VITE_HCAPTCHA_SITE_KEY ?? "10000000-ffff-ffff-ffff-000000000001";

type SubmitState = "idle" | "loading" | "success" | "error";

const RequestForm = () => {
   const [email, setEmail] = React.useState("");
   const [emailError, setEmailError] = React.useState("");
   const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
   const [submitState, setSubmitState] = React.useState<SubmitState>("idle");
   const [serverError, setServerError] = React.useState("");
   const captchaRef = React.useRef<HCaptcha>(null);

   const validateEmail = (value: string): string => {
      if (!value.trim()) return "Email is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return "Please enter a valid email address.";
      return "";
   };

   const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setEmail(val);
      if (emailError) setEmailError(validateEmail(val));
   };

   const handleEmailBlur = () => {
      setEmailError(validateEmail(email));
   };

   const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      const err = validateEmail(email);
      if (err) {
         setEmailError(err);
         return;
      }
      if (!captchaToken) {
         setServerError("Please complete the captcha.");
         return;
      }

      setSubmitState("loading");
      setServerError("");

      try {
         const res = await fetch("/api/request", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, captchaToken }),
         });
         const text = await res.text();
         const data = text ? (JSON.parse(text) as { success?: boolean; error?: string }) : {};
         if (!res.ok || !data.success) {
            throw new Error(data.error ?? `Request failed (${res.status}).`);
         }
         setSubmitState("success");
      } catch (err: unknown) {
         setSubmitState("error");
         setServerError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
         captchaRef.current?.resetCaptcha();
         setCaptchaToken(null);
      }
   };

   if (submitState === "success") {
      return (
         <Alert severity="success" sx={{ mt: 1 }}>
            Got it! You'll be added as soon as possible.
         </Alert>
      );
   }

   return (
      <Box
         component="form"
         onSubmit={handleSubmit}
         noValidate
         sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1.5,
            mt: 1.5,
         }}
      >
         <Box
            sx={{
               display: "flex",
               gap: 1,
               width: "100%"
            }}
         
         >

            <TextField
               label="Your Spotify email"
               type="email"
               value={email}
               onChange={handleEmailChange}
               onBlur={handleEmailBlur}
               error={!!emailError}
               helperText={emailError}
               disabled={submitState === "loading"}
               size="small"
               fullWidth
               sx={{
                  borderRadius: "6px",
                  backgroundColor: "transparent",
                  flex: 1,
                  // backdropFilter: "blur(5px)",
                  "& .MuiFormHelperText-root": {
                     fontWeight: 600,
                  },
                  "& .MuiInputLabel-root": {
                     color: "rgba(255,255,255,0.85)",
                  },
                  "& .MuiOutlinedInput-root": {
                     color: "#fff",
                     "& fieldset": { borderColor: "rgba(255,255,255,0.4)" },
                     "&:hover fieldset": { borderColor: "rgba(255,255,255,0.75)" },
                  },
                  "& input.MuiInputBase-input": {
                     backdropFilter: "blur(5px)"
                  }
               }}
            />
            <Button
               type="submit"
               variant="contained"
               disabled={submitState === "loading" || !captchaToken}
               sx={{ alignSelf: "center", maxWidth: "fit-content", flex: 1 }}
            >
               {submitState === "loading" ? (
                  <CircularProgress size={20} color="inherit" />
               ) : (
                  "Request Access"
               )}
            </Button>
         </Box>

         <Box sx={{ display: "flex", justifyContent: "center" }}>
            <HCaptcha
               ref={captchaRef}
               sitekey={HCAPTCHA_SITE_KEY}
               theme="dark"
               onVerify={(token) => {
                  setCaptchaToken(token);
                  setServerError("");
               }}
               onExpire={() => setCaptchaToken(null)}
            />
         </Box>

         {serverError && (
            <Typography variant="caption" sx={{ textAlign: "center", color: "#ff8a80", fontWeight: 600 }}>
               {serverError}
            </Typography>
         )}
      </Box>
   );
};

export { RequestForm };
